import { defineStore } from "pinia";
import apiService from "@/services/api";

export const useTutorialStore = defineStore("tutorial", {
  state: () => ({
    isActive: false,
    currentStep: 0,
    interactionCompleted: false, // Nova flag para controlar interação
    waitingForCommentPage: false, // Flag para auto-scroll após navegação
    steps: [
      {
        id: 1,
        target: "[data-tutorial='search']",
        title: "🔍 Buscar Artigos",
        description:
          "Use a barra de busca para encontrar posts por título ou conteúdo. Digite algo para experimentar!",
        position: "bottom",
        requiresInteraction: false,
      },
      {
        id: 2,
        target: "[data-tutorial='like']",
        title: "❤️ Curtir Posts",
        description:
          "Clique no coração para curtir este post. Vá em frente, clique!",
        position: "top",
        requiresInteraction: true,
        interactionType: "like",
      },
      {
        id: 3,
        target: "[data-tutorial='comment']",
        title: "💬 Comentar",
        description:
          "Vamos abrir este post! Escreva um comentário de teste para experimentar.",
        position: "top",
        requiresInteraction: true,
        interactionType: "comment",
        autoAction: "openPost", // Nova propriedade para ação automática
      },
      {
        id: 4,
        target: "[data-tutorial='profile']",
        title: "👤 Seu Perfil",
        description:
          "Clique no seu avatar para acessar seu perfil, editar informações e ver suas atividades.",
        position: "bottom",
        requiresInteraction: false,
      },
    ],
  }),

  getters: {
    currentStepData: (state) => state.steps[state.currentStep],
    isLastStep: (state) => state.currentStep === state.steps.length - 1,
    isFirstStep: (state) => state.currentStep === 0,
    totalSteps: (state) => state.steps.length,
    progress: (state) =>
      Math.round(((state.currentStep + 1) / state.steps.length) * 100),
  },

  actions: {
    async waitForElement(selector, timeout = 5000) {
      const startTime = Date.now();

      while (Date.now() - startTime < timeout) {
        const element = document.querySelector(selector);
        if (element) {
          console.log(`✅ Elemento encontrado: ${selector}`);
          return element;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      console.warn(`⚠️ Timeout aguardando elemento: ${selector}`);
      return null;
    },

    async startTutorial() {
      console.log("🎓 Iniciando tutorial de onboarding");

      // Aguardar primeiro step estar disponível antes de ativar
      const firstStepTarget = this.steps[0].target;
      const element = await this.waitForElement(firstStepTarget);

      if (!element) {
        console.error("❌ Tutorial não pode iniciar: elemento não encontrado");
        return;
      }

      this.isActive = true;
      this.currentStep = 0;
      this.interactionCompleted = false;
    },

    completeInteraction() {
      console.log("✅ Interação completada!");
      this.interactionCompleted = true;
    },

    nextStep() {
      if (!this.isLastStep) {
        this.currentStep++;
        this.interactionCompleted = false; // Reset para próximo step
        console.log(
          `📖 Tutorial: Passo ${this.currentStep + 1}/${this.totalSteps}`
        );
      }
    },

    previousStep() {
      if (!this.isFirstStep) {
        this.currentStep--;
        this.interactionCompleted = false;
        console.log(
          `📖 Tutorial: Passo ${this.currentStep + 1}/${this.totalSteps}`
        );
      }
    },

    async skipTutorial() {
      console.log("⏭️ Tutorial pulado pelo usuário");
      await this.completeTutorial();
    },

    async completeTutorial() {
      try {
        console.log("✅ Completando tutorial...");

        // Chamar API para marcar como completo
        await apiService.post(
          "/api/handlers?module=auth&action=complete-tutorial"
        );

        // Fechar tutorial
        this.isActive = false;
        this.currentStep = 0;
        this.interactionCompleted = false;

        console.log("🎉 Tutorial concluído com sucesso!");

        // Atualizar flag no auth store
        const authStore = useAuthStore();
        if (authStore.user) {
          authStore.user.tutorial_completed = true;
          localStorage.setItem("user", JSON.stringify(authStore.user));
        }

        return { success: true };
      } catch (error) {
        console.error("❌ Erro ao completar tutorial:", error);

        // Fechar tutorial mas alertar usuário
        this.isActive = false;
        this.interactionCompleted = false;

        // Ainda assim atualizar localmente para não bloquear
        const authStore = useAuthStore();
        if (authStore.user) {
          authStore.user.tutorial_completed = true;
          localStorage.setItem("user", JSON.stringify(authStore.user));
        }

        return {
          success: false,
          error:
            "Não foi possível salvar o progresso, mas você não precisará ver o tutorial novamente.",
        };
      }
    },

    resetTutorial() {
      console.log("🔄 Resetando tutorial");
      this.isActive = false;
      this.currentStep = 0;
      this.interactionCompleted = false;
      this.waitingForCommentPage = false;
    },

    enableCommentAutoScroll() {
      console.log("🎯 Ativando auto-scroll para comentário");
      this.waitingForCommentPage = true;
    },

    async handleCommentPageLoaded() {
      if (!this.waitingForCommentPage) return;

      console.log("📄 Página do post carregada, fazendo auto-scroll...");
      this.waitingForCommentPage = false;

      // Aguardar DOM carregar completamente
      await new Promise((resolve) => setTimeout(resolve, 800));

      // ATUALIZAR o target do step atual para o campo de comentário
      if (this.steps[this.currentStep]) {
        this.steps[this.currentStep].target = "[data-tutorial='comment-input']";
        console.log("🎯 Target atualizado para campo de comentário");
      }

      // Buscar campo de comentário
      const commentInput =
        document.querySelector('textarea[placeholder*="comentário"]') ||
        document.querySelector('textarea[placeholder*="Comentário"]') ||
        document.querySelector("[data-tutorial='comment-input']");

      if (commentInput) {
        console.log("📝 Campo encontrado! Fazendo scroll...");

        // Scroll suave até o campo
        commentInput.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Aguardar scroll completar
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Focar no campo
        commentInput.focus();

        // MARCAR como comentário de tutorial (não será salvo no BD)
        commentInput.dataset.tutorialComment = "true";

        // Aguardar um momento e disparar evento para recalcular posições do tutorial
        await new Promise((resolve) => setTimeout(resolve, 300));
        window.dispatchEvent(new Event("tutorial-update-positions"));

        console.log("✅ Digite um comentário de teste!");

        // Detectar quando usuário digitar
        const handleInput = () => {
          if (commentInput.value.trim().length > 3) {
            console.log("✅ Comentário detectado!");
            commentInput.removeEventListener("input", handleInput);

            setTimeout(() => {
              this.completeInteraction();
            }, 1500);
          }
        };

        commentInput.addEventListener("input", handleInput);
      } else {
        console.warn("⚠️ Campo não encontrado");
        setTimeout(() => this.completeInteraction(), 2000);
      }
    },
  },
});

// Import auth store
import { useAuthStore } from "./auth";
